import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
} from "reactstrap";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import Flatpickr from "react-flatpickr";
import BreadCrumb from "components/common/BreadCrumb";
import { BackButton } from "components/common/Buttons";
import { AsyncPaginatedSelect } from "components/common/AsyncPaginatedSelect";
import PageLoadingSpinner from "components/common/PageLoader";
import { getDocumentTitle } from "utils/helpers";
import { getClientOptions } from "features/Clients/services/ClientService";
import { getMaterialOptions } from "features/Materials/services/MaterialService";
import {
  ComplaintMaterialSelectionType,
  ComplaintMaterialSelectionTypeLabel,
  ComplaintMaterialWarrantyStatus,
  ComplaintMaterialWarrantyStatusLabel,
} from "features/ComplaintMaterials/constants/complaint-material.constants";
import type { ComplaintMaterial } from "features/ComplaintMaterials/types/complaint-material.types";
import {
  createComplaintMaterial,
  getComplaintMaterial,
  updateComplaintMaterial,
} from "features/ComplaintMaterials/services/ComplaintMaterialService";
import schema from "./schema";

const LIST_PATH = "/complaint-materials";

function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Normalizes a date value to plain `YYYY-MM-DD` whether the API sends that or a full ISO timestamp. */
function toDateOnly(value: string | null | undefined): string {
  if (!value) return "";
  const match = /^\d{4}-\d{2}-\d{2}/.exec(value);
  return match ? match[0] : "";
}

const ComplaintMaterialForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  document.title = getDocumentTitle(
    isEdit ? "Edit Complaint Material" : "New Complaint Material",
  );
  const navigate = useNavigate();

  const [record, setRecord] = useState<ComplaintMaterial | null>(null);
  const [loadingRecord, setLoadingRecord] = useState(isEdit);

  const [clientDefaultOption, setClientDefaultOption] = useState<
    { value: number; label: string } | undefined
  >(undefined);
  const [materialDefaultOption, setMaterialDefaultOption] = useState<
    { value: number; label: string } | undefined
  >(undefined);

  useEffect(() => {
    if (!isEdit || !id) {
      setLoadingRecord(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingRecord(true);
      try {
        const res = await getComplaintMaterial(Number(id));
        if (cancelled) return;
        if (res.success && res.data) {
          setRecord(res.data);
          if (res.data.client) {
            setClientDefaultOption({
              value: res.data.client.id,
              label: res.data.client.name?.trim() || `Client #${res.data.client.id}`,
            });
          }
          if (res.data.material) {
            setMaterialDefaultOption({
              value: res.data.material.id,
              label: res.data.material.name,
            });
          }
        } else {
          toast.error(res.message || "Could not load record");
          navigate(LIST_PATH, { replace: true });
        }
      } catch (err) {
        console.warn(err);
        if (!cancelled) {
          toast.error("Could not load record");
          navigate(LIST_PATH, { replace: true });
        }
      } finally {
        if (!cancelled) setLoadingRecord(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, id, navigate]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      clientId: record?.clientId ?? "",
      selectionType:
        record?.selectionType ?? ComplaintMaterialSelectionType.MATERIAL,
      materialId: record?.materialId ?? "",
      name: record?.name ?? "",
      manufacturer: record?.manufacturer ?? "",
      serialNo: record?.serialNo ?? "",
      purchaseDate: toDateOnly(record?.purchaseDate),
      warrantyStatus:
        record?.warrantyStatus ?? ComplaintMaterialWarrantyStatus.IN_WARRANTY,
      complaintReceivedDate: toDateOnly(record?.complaintReceivedDate),
      complaintDescription: record?.complaintDescription ?? "",
      remarks: record?.remarks ?? "",
      resolvedDate: toDateOnly(record?.resolvedDate),
      dateOfDispatch: toDateOnly(record?.dateOfDispatch),
      dateHandedBackToClient: toDateOnly(record?.dateHandedBackToClient),
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      const isOther = values.selectionType === ComplaintMaterialSelectionType.OTHER;
      const writeFields = {
        materialId: isOther ? undefined : Number(values.materialId),
        name: isOther ? values.name.trim() : undefined,
        manufacturer: values.manufacturer.trim() || undefined,
        serialNo: values.serialNo.trim() || undefined,
        purchaseDate: values.purchaseDate || undefined,
        warrantyStatus: values.warrantyStatus as ComplaintMaterialWarrantyStatus,
        complaintReceivedDate: values.complaintReceivedDate || undefined,
        complaintDescription: values.complaintDescription.trim(),
        remarks: values.remarks.trim() || undefined,
        /** Sent as explicit null when blank so clearing an existing date on edit actually clears it. */
        resolvedDate: values.resolvedDate || null,
        dateOfDispatch: values.dateOfDispatch || null,
        dateHandedBackToClient: values.dateHandedBackToClient || null,
      };

      try {
        if (isEdit && record) {
          const res = await updateComplaintMaterial(record.id, {
            clientId: Number(values.clientId),
            ...writeFields,
          });
          if (res.success) {
            toast.success(res.message);
            navigate(`/complaint-materials/${record.id}`);
          } else {
            toast.error(res.message ?? "Could not update record.");
          }
        } else {
          const res = await createComplaintMaterial({
            clientId: Number(values.clientId),
            selectionType: values.selectionType as ComplaintMaterialSelectionType,
            ...writeFields,
          });
          if (res.success) {
            toast.success(res.message);
            navigate(res.data ? `/complaint-materials/${res.data.id}` : LIST_PATH);
          } else {
            toast.error(res.message ?? "Could not create record.");
          }
        }
      } catch (err: any) {
        toast.error(
          err?.response?.data?.message ??
            err?.message ??
            (isEdit ? "Failed to update record" : "Failed to create record"),
        );
      }
    },
  });

  const isOtherSource =
    formik.values.selectionType === ComplaintMaterialSelectionType.OTHER;

  if (loadingRecord) {
    return <PageLoadingSpinner message="Loading…" />;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={isEdit ? "Edit Complaint Material" : "New Complaint Material"}
          pageTitle="Complaint Materials"
          items={[{ label: "Complaint Materials", path: LIST_PATH }]}
        />
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            formik.handleSubmit();
          }}
        >
          <Row className="g-4">
            <Col xs={12}>
              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">Client &amp; item</h5>
                </CardHeader>
                <CardBody>
                  <Row className="g-3">
                    <Col md={6}>
                      <div className="d-flex align-items-center justify-content-between">
                        <Label className="form-label mb-0">
                          Client <span className="text-danger">*</span>
                        </Label>
                        <a
                          href="/clients/create"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-link btn-sm p-0 mb-1"
                        >
                          <i className="ri-add-line align-bottom" /> New client
                        </a>
                      </div>
                      <AsyncPaginatedSelect
                        fetchOptions={getClientOptions}
                        defaultOption={clientDefaultOption}
                        placeholder="Search clients…"
                        value={formik.values.clientId}
                        onChange={(val: any) => {
                          formik.setFieldValue("clientId", val?.value ?? "");
                          setClientDefaultOption(
                            val ? { value: val.value, label: val.label } : undefined,
                          );
                        }}
                        onBlur={() => formik.setFieldTouched("clientId", true)}
                        isInvalid={
                          Boolean(formik.touched.clientId) && !!formik.errors.clientId
                        }
                      />
                      {formik.touched.clientId && formik.errors.clientId ? (
                        <FormFeedback type="invalid" className="d-block">
                          {String(formik.errors.clientId)}
                        </FormFeedback>
                      ) : null}
                    </Col>

                    <Col md={6}>
                      <Label className="form-label">
                        Warranty status <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="select"
                        name="warrantyStatus"
                        value={formik.values.warrantyStatus}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      >
                        {(
                          Object.values(
                            ComplaintMaterialWarrantyStatus,
                          ) as ComplaintMaterialWarrantyStatus[]
                        ).map((w) => (
                          <option key={w} value={w}>
                            {ComplaintMaterialWarrantyStatusLabel[w]}
                          </option>
                        ))}
                      </Input>
                    </Col>

                    <Col md={12}>
                      <Label className="form-label">Item source</Label>
                      <div className="d-flex gap-4 pt-1 flex-wrap">
                        {(
                          Object.values(
                            ComplaintMaterialSelectionType,
                          ) as ComplaintMaterialSelectionType[]
                        ).map((opt) => (
                          <div className="form-check" key={opt}>
                            <Input
                              type="radio"
                              className="form-check-input"
                              id={`cm-selection-type-${opt}`}
                              name="selectionType"
                              value={opt}
                              checked={formik.values.selectionType === opt}
                              disabled={isEdit}
                              onChange={(e) => {
                                formik.setFieldValue("selectionType", e.target.value);
                                formik.setFieldValue("materialId", "");
                                formik.setFieldValue("name", "");
                              }}
                            />
                            <Label
                              className="form-check-label"
                              for={`cm-selection-type-${opt}`}
                            >
                              {ComplaintMaterialSelectionTypeLabel[opt]}
                            </Label>
                          </div>
                        ))}
                      </div>
                      {isEdit ? (
                        <small className="text-muted d-block mt-1">
                          The item source can&apos;t be changed after creation.
                        </small>
                      ) : null}
                    </Col>

                    <Col md={6}>
                      {isOtherSource ? (
                        <>
                          <Label className="form-label" for="cm-name">
                            Item name <span className="text-danger">*</span>
                          </Label>
                          <Input
                            id="cm-name"
                            name="name"
                            placeholder="Client-supplied item name"
                            value={formik.values.name}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={Boolean(
                              formik.touched.name && formik.errors.name,
                            )}
                          />
                          {formik.touched.name && formik.errors.name ? (
                            <FormFeedback type="invalid">
                              {String(formik.errors.name)}
                            </FormFeedback>
                          ) : null}
                        </>
                      ) : (
                        <>
                          <Label className="form-label">
                            Material <span className="text-danger">*</span>
                          </Label>
                          <AsyncPaginatedSelect
                            fetchOptions={getMaterialOptions}
                            defaultOption={materialDefaultOption}
                            placeholder="Search materials…"
                            value={formik.values.materialId}
                            isDisabled={isEdit}
                            onChange={(val: any) => {
                              formik.setFieldValue("materialId", val?.value ?? "");
                              setMaterialDefaultOption(
                                val
                                  ? { value: val.value, label: val.label }
                                  : undefined,
                              );
                            }}
                            onBlur={() =>
                              formik.setFieldTouched("materialId", true)
                            }
                            isInvalid={
                              Boolean(formik.touched.materialId) &&
                              !!formik.errors.materialId
                            }
                          />
                          {formik.touched.materialId && formik.errors.materialId ? (
                            <FormFeedback type="invalid" className="d-block">
                              {String(formik.errors.materialId)}
                            </FormFeedback>
                          ) : null}
                        </>
                      )}
                    </Col>
                    <Col md={6}>
                      <Label className="form-label" for="cm-manufacturer">
                        Manufacturer{" "}
                        <span className="text-muted fw-normal">(optional)</span>
                      </Label>
                      <Input
                        id="cm-manufacturer"
                        name="manufacturer"
                        value={formik.values.manufacturer}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                    </Col>

                    <Col md={6}>
                      <Label className="form-label" for="cm-serial-no">
                        Serial no.{" "}
                        <span className="text-muted fw-normal">(optional)</span>
                      </Label>
                      <Input
                        id="cm-serial-no"
                        name="serialNo"
                        value={formik.values.serialNo}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                    </Col>
                    <Col md={6}>
                      <Label className="form-label">
                        Purchase date{" "}
                        <span className="text-muted fw-normal">(optional)</span>
                      </Label>
                      <Flatpickr
                        className="form-control"
                        placeholder="Select date"
                        options={{ altInput: true, altFormat: "d M, Y", dateFormat: "Y-m-d" }}
                        value={
                          formik.values.purchaseDate
                            ? new Date(`${formik.values.purchaseDate}T12:00:00`)
                            : ""
                        }
                        onChange={(dates: Date[]) =>
                          formik.setFieldValue(
                            "purchaseDate",
                            dates?.[0] ? toYmd(dates[0]) : "",
                          )
                        }
                      />
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">Complaint</h5>
                </CardHeader>
                <CardBody>
                  <Row className="g-3">
                    <Col md={6}>
                      <Label className="form-label">
                        Complaint received date{" "}
                        <span className="text-muted fw-normal">
                          (defaults to now)
                        </span>
                      </Label>
                      <Flatpickr
                        className="form-control"
                        placeholder="Defaults to now"
                        options={{ altInput: true, altFormat: "d M, Y", dateFormat: "Y-m-d" }}
                        value={
                          formik.values.complaintReceivedDate
                            ? new Date(
                                `${formik.values.complaintReceivedDate}T12:00:00`,
                              )
                            : ""
                        }
                        onChange={(dates: Date[]) =>
                          formik.setFieldValue(
                            "complaintReceivedDate",
                            dates?.[0] ? toYmd(dates[0]) : "",
                          )
                        }
                      />
                    </Col>
                    <Col md={12}>
                      <Label className="form-label" for="cm-description">
                        Complaint description{" "}
                        <span className="text-danger">*</span>
                      </Label>
                      <Input
                        id="cm-description"
                        type="textarea"
                        rows={3}
                        name="complaintDescription"
                        value={formik.values.complaintDescription}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={Boolean(
                          formik.touched.complaintDescription &&
                            formik.errors.complaintDescription,
                        )}
                      />
                      {formik.touched.complaintDescription &&
                      formik.errors.complaintDescription ? (
                        <FormFeedback type="invalid">
                          {String(formik.errors.complaintDescription)}
                        </FormFeedback>
                      ) : null}
                    </Col>
                    <Col md={12}>
                      <Label className="form-label" for="cm-remarks">
                        Remarks{" "}
                        <span className="text-muted fw-normal">(optional)</span>
                      </Label>
                      <Input
                        id="cm-remarks"
                        type="textarea"
                        rows={2}
                        name="remarks"
                        value={formik.values.remarks}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">Lifecycle dates</h5>
                  <p className="text-muted small mb-0 mt-1">
                    Set, change, or clear any of these in any order — status
                    is derived automatically from whichever are filled in.
                  </p>
                </CardHeader>
                <CardBody>
                  <Row className="g-3">
                    <Col md={4}>
                      <Label className="form-label">
                        Resolved date{" "}
                        <span className="text-muted fw-normal">(optional)</span>
                      </Label>
                      <Flatpickr
                        className="form-control"
                        placeholder="Not set"
                        options={{ altInput: true, altFormat: "d M, Y", dateFormat: "Y-m-d" }}
                        value={
                          formik.values.resolvedDate
                            ? new Date(`${formik.values.resolvedDate}T12:00:00`)
                            : ""
                        }
                        onChange={(dates: Date[]) =>
                          formik.setFieldValue(
                            "resolvedDate",
                            dates?.[0] ? toYmd(dates[0]) : "",
                          )
                        }
                      />
                      {formik.values.resolvedDate ? (
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 mt-1"
                          onClick={() => formik.setFieldValue("resolvedDate", "")}
                        >
                          Clear
                        </button>
                      ) : null}
                    </Col>
                    <Col md={4}>
                      <Label className="form-label">
                        Date of dispatch{" "}
                        <span className="text-muted fw-normal">(optional)</span>
                      </Label>
                      <Flatpickr
                        className="form-control"
                        placeholder="Not set"
                        options={{ altInput: true, altFormat: "d M, Y", dateFormat: "Y-m-d" }}
                        value={
                          formik.values.dateOfDispatch
                            ? new Date(`${formik.values.dateOfDispatch}T12:00:00`)
                            : ""
                        }
                        onChange={(dates: Date[]) =>
                          formik.setFieldValue(
                            "dateOfDispatch",
                            dates?.[0] ? toYmd(dates[0]) : "",
                          )
                        }
                      />
                      {formik.values.dateOfDispatch ? (
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 mt-1"
                          onClick={() => formik.setFieldValue("dateOfDispatch", "")}
                        >
                          Clear
                        </button>
                      ) : null}
                    </Col>
                    <Col md={4}>
                      <Label className="form-label">
                        Handed back to client{" "}
                        <span className="text-muted fw-normal">(optional)</span>
                      </Label>
                      <Flatpickr
                        className="form-control"
                        placeholder="Not set"
                        options={{ altInput: true, altFormat: "d M, Y", dateFormat: "Y-m-d" }}
                        value={
                          formik.values.dateHandedBackToClient
                            ? new Date(
                                `${formik.values.dateHandedBackToClient}T12:00:00`,
                              )
                            : ""
                        }
                        onChange={(dates: Date[]) =>
                          formik.setFieldValue(
                            "dateHandedBackToClient",
                            dates?.[0] ? toYmd(dates[0]) : "",
                          )
                        }
                      />
                      {formik.values.dateHandedBackToClient ? (
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 mt-1"
                          onClick={() =>
                            formik.setFieldValue("dateHandedBackToClient", "")
                          }
                        >
                          Clear
                        </button>
                      ) : null}
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <div className="d-flex flex-wrap justify-content-end gap-3 mb-4">
            <BackButton />
            <button
              type="submit"
              className="btn btn-primary w-sm"
              disabled={formik.isSubmitting}
            >
              {formik.isSubmitting
                ? isEdit
                  ? "Saving…"
                  : "Creating…"
                : isEdit
                  ? "Save changes"
                  : "Create"}
            </button>
          </div>
        </Form>
      </Container>
    </div>
  );
};

export default ComplaintMaterialForm;
